import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  User, Smartphone, Save, Heart,
  Settings, Flame, Search, Camera, Loader
} from 'lucide-react';
import axios from 'axios';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`padding: 20px; display: flex; flex-direction: column; gap: 20px;`;

const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
`;
const Title = styled.h1`font-size: 22px; font-weight: 800; color: var(--text-primary); display: flex; align-items: center; gap: 10px;`;

const ProfileCard = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 24px; display: flex; align-items: center; gap: 20px;
`;

const Avatar = styled.div`
  width: 64px; height: 64px; border-radius: 50%;
  background: linear-gradient(135deg, #FE3C72, #FF6B6B);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 24px; font-weight: 700; flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const DetectBtn = styled.button`
  padding: 8px 16px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
  border: 1px solid rgba(254,60,114,.3); background: rgba(254,60,114,.08);
  color: #FE3C72; display: flex; align-items: center; gap: 6px;
  transition: all .15s;
  &:hover { background: rgba(254,60,114,.15); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const DetectStatus = styled.div`
  margin-top: 8px; padding: 8px 12px; border-radius: 8px; font-size: 12px;
  background: ${p => p.$error ? 'rgba(239,68,68,.08)' : 'rgba(34,197,94,.08)'};
  color: ${p => p.$error ? '#ef4444' : '#22c55e'};
  border: 1px solid ${p => p.$error ? 'rgba(239,68,68,.2)' : 'rgba(34,197,94,.2)'};
`;

const ToggleRow = styled.label`
  display: flex; align-items: center; gap: 8px; cursor: pointer;
  font-size: 12px; color: var(--text-muted); padding: 6px 0;
  input { accent-color: #FE3C72; }
`;

const ProfileMeta = styled.div`
  flex: 1;
  h2 { font-size: 18px; font-weight: 700; color: var(--text-primary); margin: 0 0 6px; }
`;

const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
`;

const StatBox = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 10px;
  padding: 14px; text-align: center;
  .value { font-size: 20px; font-weight: 800; color: var(--text-primary); }
  .label { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
`;

const Section = styled.div`
  background: var(--card-bg); border: 1px solid var(--card-border); border-radius: 14px;
  padding: 20px;
  h3 { font-size: 15px; font-weight: 700; color: var(--text-primary); margin: 0 0 14px; display: flex; align-items: center; gap: 8px; }
`;

const FormRow = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  label { display: block; font-size: 12px; font-weight: 600; color: var(--text-muted); margin-bottom: 4px; }
  input, select { width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid var(--card-border);
    background: var(--bg); color: var(--text-primary); font-size: 13px; outline: none;
    &:focus { border-color: #FE3C72; }
  }
`;

const Btn = styled.button`
  padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer;
  border: none; display: flex; align-items: center; gap: 6px;
  background: ${p => p.$primary ? 'linear-gradient(135deg, #FE3C72, #FF6B6B)' : 'var(--card-bg)'};
  color: ${p => p.$primary ? '#fff' : 'var(--text-primary)'};
  border: 1px solid ${p => p.$primary ? 'transparent' : 'var(--card-border)'};
  &:hover { opacity: .85; }
`;

const Badge = styled.span`
  padding: 4px 10px; border-radius: 12px; font-size: 11px; font-weight: 700;
  background: rgba(254,60,114,.12); color: #FE3C72;
`;

const NoDevice = styled.div`
  text-align: center; padding: 60px 20px; color: var(--text-muted);
  svg { margin-bottom: 12px; opacity: .4; }
  h3 { font-size: 16px; font-weight: 600; color: var(--text-primary); margin-bottom: 6px; }
`;

/* ═══════════════════════════════════════════════ */

const TinderAccount = ({ selectedDevice }) => {
  const [, setAccount] = useState(null);
  const [form, setForm] = useState({
    account_name: '', tinder_id: '', bio: '', age: '', city: '',
    subscription: 'free', speed_level: 2, like_ratio: 0.5,
    session_max_actions: 100, session_max_mins: 45,
    daily_like_limit: 100, daily_super_like_limit: 5, daily_message_limit: 50,
    profile_taps_min: 0, profile_taps_max: 3,
    tap_pause_min: 0.5, tap_pause_max: 2.0,
    action_delay_min: 3.0, action_delay_max: 8.0
  });
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [capturePhoto, setCapturePhoto] = useState(true);
  const [detectResult, setDetectResult] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState(null);

  const deviceId = selectedDevice?.device_id;

  useEffect(() => {
    if (!deviceId) return;
    axios.get(`/api/tinder-scripts/account/${deviceId}`)
      .then(r => {
        if (r.data.account) {
          setAccount(r.data.account);
          setForm(prev => ({ ...prev, ...r.data.account }));
          if (r.data.account.profile_pic_url) {
            setProfilePicUrl(`/api/tinder-scripts/profile-photo/${deviceId}?t=${Date.now()}`);
          }
        }
      })
      .catch(() => {});
  }, [deviceId]);

  const handleDetect = async () => {
    if (!deviceId || detecting) return;
    setDetecting(true);
    setDetectResult(null);
    try {
      const r = await axios.post('/api/tinder-scripts/detect-account', {
        device_id: deviceId,
        capture_photo: capturePhoto,
      });
      if (r.data.success) {
        setDetectResult({ msg: `Compte détecté : ${r.data.account_name}`, error: false });
        // Reload account data
        const acctRes = await axios.get(`/api/tinder-scripts/account/${deviceId}`);
        if (acctRes.data.account) {
          setAccount(acctRes.data.account);
          setForm(prev => ({ ...prev, ...acctRes.data.account }));
          if (acctRes.data.account.profile_pic_url) {
            setProfilePicUrl(`/api/tinder-scripts/profile-photo/${deviceId}?t=${Date.now()}`);
          }
        }
      } else {
        setDetectResult({ msg: r.data.error || 'Détection échouée', error: true });
      }
    } catch (e) {
      setDetectResult({ msg: e.response?.data?.error || e.message, error: true });
    }
    setDetecting(false);
  };

  const handleSave = async () => {
    if (!deviceId || !form.account_name) return;
    setSaving(true);
    try {
      await axios.put(`/api/tinder-scripts/account/${deviceId}`, form);
      setAccount(form);
    } catch (e) {
      console.error('Save error:', e);
    }
    setSaving(false);
  };

  if (!deviceId) {
    return (
      <Page>
        <NoDevice>
          <Smartphone size={48} />
          <h3>Aucun appareil sélectionné</h3>
          <p>Sélectionnez un appareil dans la sidebar pour configurer son compte Tinder.</p>
        </NoDevice>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <Title><Flame size={24} color="#FE3C72" /> Compte Tinder</Title>
        <Badge>Bêta</Badge>
      </Header>

      <ProfileCard>
        <Avatar>
          {profilePicUrl
            ? <img src={profilePicUrl} alt={form.account_name} onError={() => setProfilePicUrl(null)} />
            : (form.account_name ? form.account_name[0].toUpperCase() : <User size={28} />)
          }
        </Avatar>
        <ProfileMeta>
          <h2>{form.account_name || 'Non configuré'}</h2>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {selectedDevice.device_name || deviceId} • {form.subscription || 'free'}
          </div>
        </ProfileMeta>
        <DetectBtn onClick={handleDetect} disabled={detecting}>
          {detecting ? <Loader size={13} className="spin" /> : <Search size={13} />}
          {detecting ? 'Détection...' : 'Détecter le compte'}
        </DetectBtn>
        <Btn $primary onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Sauvegarder'}
        </Btn>
      </ProfileCard>

      {detectResult && (
        <DetectStatus $error={detectResult.error}>{detectResult.msg}</DetectStatus>
      )}

      <Section>
        <h3><Camera size={16} color="#FE3C72" /> Détection automatique</h3>
        <ToggleRow>
          <input type="checkbox" checked={capturePhoto} onChange={e => setCapturePhoto(e.target.checked)} />
          Capturer la photo de profil lors de la détection (navigue vers l'onglet profil Tinder)
        </ToggleRow>
      </Section>

      <StatsRow>
        <StatBox><div className="value">0</div><div className="label">Likes</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Nopes</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Matches</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Messages</div></StatBox>
        <StatBox><div className="value">0</div><div className="label">Sessions</div></StatBox>
      </StatsRow>

      <Section>
        <h3><User size={16} color="#FE3C72" /> Informations du compte</h3>
        <FormRow>
          <Field>
            <label>Nom du compte</label>
            <input value={form.account_name} onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))} placeholder="Nom ou numéro" />
          </Field>
          <Field>
            <label>Tinder ID</label>
            <input value={form.tinder_id} onChange={e => setForm(f => ({ ...f, tinder_id: e.target.value }))} placeholder="ID Tinder (optionnel)" />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Âge</label>
            <input type="number" value={form.age || ''} onChange={e => setForm(f => ({ ...f, age: parseInt(e.target.value) || '' }))} placeholder="25" />
          </Field>
          <Field>
            <label>Ville</label>
            <input value={form.city || ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder="Paris" />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Abonnement</label>
            <select value={form.subscription} onChange={e => setForm(f => ({ ...f, subscription: e.target.value }))}>
              <option value="free">Free</option>
              <option value="plus">Tinder Plus</option>
              <option value="gold">Tinder Gold</option>
              <option value="platinum">Tinder Platinum</option>
            </select>
          </Field>
          <Field>
            <label>Bio</label>
            <input value={form.bio || ''} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} placeholder="Bio Tinder..." />
          </Field>
        </FormRow>
      </Section>

      <Section>
        <h3><Settings size={16} color="#FE3C72" /> Limites d'automatisation</h3>
        <FormRow>
          <Field>
            <label>Vitesse (1-5)</label>
            <select value={form.speed_level} onChange={e => setForm(f => ({ ...f, speed_level: parseInt(e.target.value) }))}>
              {[1,2,3,4,5].map(v => <option key={v} value={v}>{v} — {['Très lent','Lent','Normal','Rapide','Très rapide'][v-1]}</option>)}
            </select>
          </Field>
          <Field>
            <label>Ratio Like ({Math.round((form.like_ratio || 0.5) * 100)}%)</label>
            <input type="range" min="0" max="100" value={Math.round((form.like_ratio || 0.5) * 100)}
              onChange={e => setForm(f => ({ ...f, like_ratio: parseInt(e.target.value) / 100 }))} />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Max actions/session</label>
            <input type="number" value={form.session_max_actions} onChange={e => setForm(f => ({ ...f, session_max_actions: parseInt(e.target.value) || 100 }))} />
          </Field>
          <Field>
            <label>Max minutes/session</label>
            <input type="number" value={form.session_max_mins} onChange={e => setForm(f => ({ ...f, session_max_mins: parseInt(e.target.value) || 45 }))} />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Limite likes/jour</label>
            <input type="number" value={form.daily_like_limit} onChange={e => setForm(f => ({ ...f, daily_like_limit: parseInt(e.target.value) || 100 }))} />
          </Field>
          <Field>
            <label>Limite super likes/jour</label>
            <input type="number" value={form.daily_super_like_limit} onChange={e => setForm(f => ({ ...f, daily_super_like_limit: parseInt(e.target.value) || 5 }))} />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Limite messages/jour</label>
            <input type="number" value={form.daily_message_limit} onChange={e => setForm(f => ({ ...f, daily_message_limit: parseInt(e.target.value) || 50 }))} />
          </Field>
        </FormRow>
      </Section>

      <Section>
        <h3><Heart size={16} color="#FE3C72" /> Paramètres de swipe</h3>
        <FormRow>
          <Field>
            <label>Taps min sur profil (0-5)</label>
            <input type="number" min="0" max="5" value={form.profile_taps_min}
              onChange={e => {
                const v = Math.max(0, Math.min(5, parseInt(e.target.value) || 0));
                setForm(f => ({ ...f, profile_taps_min: v, profile_taps_max: Math.max(v, f.profile_taps_max) }));
              }} />
          </Field>
          <Field>
            <label>Taps max sur profil (0-5)</label>
            <input type="number" min="0" max="5" value={form.profile_taps_max}
              onChange={e => {
                const v = Math.max(0, Math.min(5, parseInt(e.target.value) || 0));
                setForm(f => ({ ...f, profile_taps_max: v, profile_taps_min: Math.min(v, f.profile_taps_min) }));
              }} />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Pause min entre taps (s)</label>
            <input type="number" min="0.1" max="10" step="0.1" value={form.tap_pause_min}
              onChange={e => {
                const v = parseFloat(e.target.value) || 0.5;
                setForm(f => ({ ...f, tap_pause_min: v, tap_pause_max: Math.max(v, f.tap_pause_max) }));
              }} />
          </Field>
          <Field>
            <label>Pause max entre taps (s)</label>
            <input type="number" min="0.1" max="10" step="0.1" value={form.tap_pause_max}
              onChange={e => {
                const v = parseFloat(e.target.value) || 2.0;
                setForm(f => ({ ...f, tap_pause_max: v, tap_pause_min: Math.min(v, f.tap_pause_min) }));
              }} />
          </Field>
        </FormRow>
        <FormRow>
          <Field>
            <label>Délai min entre swipes (s)</label>
            <input type="number" min="0.5" max="30" step="0.5" value={form.action_delay_min}
              onChange={e => {
                const v = parseFloat(e.target.value) || 3.0;
                setForm(f => ({ ...f, action_delay_min: v, action_delay_max: Math.max(v, f.action_delay_max) }));
              }} />
          </Field>
          <Field>
            <label>Délai max entre swipes (s)</label>
            <input type="number" min="1" max="60" step="0.5" value={form.action_delay_max}
              onChange={e => {
                const v = parseFloat(e.target.value) || 8.0;
                setForm(f => ({ ...f, action_delay_max: v, action_delay_min: Math.min(v, f.action_delay_min) }));
              }} />
          </Field>
        </FormRow>
      </Section>
    </Page>
  );
};

export default TinderAccount;
